<template>
  <Transition name="fade">
    <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto">
      <!-- Overlay -->
      <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" @click="handleOverlayClick"></div>
      
      <!-- Dialog -->
      <div class="flex min-h-full items-center justify-center p-4">
        <div class="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
          <!-- Header -->
          <div class="px-6 py-4 border-b border-gray-200">
            <div class="flex items-center">
              <!-- Icône selon le type -->
              <div class="flex-shrink-0 mr-3">
                <div v-if="props.isFirstLogin" class="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                  </svg>
                </div>
                <div v-else class="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                  <svg class="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                  </svg>
                </div>
              </div>
              
              <!-- Titre -->
              <h3 class="text-lg font-medium text-gray-900">
                {{ props.isFirstLogin ? 'Bienvenue ! Définissez votre mot de passe' : 'Changement de mot de passe requis' }}
              </h3>
            </div>
          </div>

      <!-- Body -->
      <div class="px-6 py-4">
        <div class="mb-4">
          <p class="text-sm text-gray-600">
            {{ props.isFirstLogin 
              ? 'Bienvenue ! Pour des raisons de sécurité, vous devez définir votre mot de passe avant de continuer.' 
              : 'Pour des raisons de sécurité, vous devez changer votre mot de passe avant de continuer.' 
            }}
          </p>
          <div v-if="passwordExpiresAt" class="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p class="text-sm text-yellow-800">
              <strong>Attention :</strong> Votre mot de passe actuel expire le 
              {{ formatDate(passwordExpiresAt) }}.
            </p>
          </div>
        </div>

        <form @submit.prevent="handlePasswordChange" class="space-y-4">
          <!-- Mot de passe actuel -->
          <div>
            <label for="currentPassword" class="block text-sm font-medium text-gray-700">
              Mot de passe actuel
            </label>
            <div class="mt-1 relative">
              <input
                id="currentPassword"
                v-model="form.currentPassword"
                :type="showCurrentPassword ? 'text' : 'password'"
                required
                class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Entrez votre mot de passe actuel"
              />
              <button
                type="button"
                @click="showCurrentPassword = !showCurrentPassword"
                class="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <svg v-if="showCurrentPassword" class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                </svg>
                <svg v-else class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
            </div>
          </div>

              <!-- Nouveau mot de passe -->
              <div>
                <div class="flex items-center justify-between">
                  <label for="newPassword" class="block text-sm font-medium text-gray-700">
                    Nouveau mot de passe
                  </label>
                  <button
                    type="button"
                    @click="generatePassword"
                    class="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center"
                  >
                    <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Générer
                  </button>
                </div>
                <div class="mt-1 relative">
                  <input
                    id="newPassword"
                    v-model="form.newPassword"
                    :type="showNewPassword ? 'text' : 'password'"
                    required
                    class="block w-full px-3 py-2 pr-20 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Entrez votre nouveau mot de passe"
                  />
                  <div class="absolute inset-y-0 right-0 flex items-center pr-1">
                    <button
                      type="button"
                      @click="showNewPassword = !showNewPassword"
                      class="p-1 text-gray-400 hover:text-gray-600"
                      :title="showNewPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'"
                    >
                      <svg v-if="showNewPassword" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                      <svg v-else class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                  </div>
                </div>
                
                <!-- Indicateur de puissance du mot de passe -->
                <div v-if="form.newPassword" class="mt-2">
                  <div class="flex items-center justify-between text-xs text-gray-600 mb-1">
                    <span>Force du mot de passe</span>
                    <span class="font-medium" :class="passwordStrength.color">{{ passwordStrength.label }}</span>
                  </div>
                  <div class="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      class="h-2 rounded-full transition-all duration-300"
                      :class="passwordStrength.bgColor"
                      :style="{ width: passwordStrength.percentage + '%' }"
                    ></div>
                  </div>
                  <div class="mt-1 text-xs text-gray-500">
                    {{ passwordStrength.requirements }}
                  </div>
                </div>
              </div>

          <!-- Confirmation du mot de passe -->
          <div>
            <label for="confirmPassword" class="block text-sm font-medium text-gray-700">
              Confirmer le nouveau mot de passe
            </label>
            <div class="mt-1 relative">
              <input
                id="confirmPassword"
                v-model="form.confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                required
                class="block w-full px-3 py-2 pr-12 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Confirmez votre nouveau mot de passe"
              />
              <div class="absolute inset-y-0 right-0 flex items-center pr-1">
                <button
                  type="button"
                  @click="showConfirmPassword = !showConfirmPassword"
                  class="p-1 text-gray-400 hover:text-gray-600"
                  :title="showConfirmPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'"
                >
                  <svg v-if="showConfirmPassword" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                  </svg>
                  <svg v-else class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
              </div>
            </div>
            
            <!-- Indicateur de correspondance -->
            <div v-if="form.confirmPassword" class="mt-1 flex items-center text-xs">
              <svg v-if="form.newPassword === form.confirmPassword" class="h-4 w-4 text-green-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <svg v-else class="h-4 w-4 text-red-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span :class="form.newPassword === form.confirmPassword ? 'text-green-600' : 'text-red-600'">
                {{ form.newPassword === form.confirmPassword ? 'Les mots de passe correspondent' : 'Les mots de passe ne correspondent pas' }}
              </span>
            </div>
          </div>

          <!-- Message d'erreur -->
          <div v-if="error" class="p-3 bg-red-50 border border-red-200 rounded-md">
            <p class="text-sm text-red-800">{{ error }}</p>
          </div>
        </form>
      </div>

          <!-- Actions -->
          <div class="px-6 py-4 bg-gray-50 flex justify-end space-x-3">
            <button
              v-if="!props.isFirstLogin"
              @click="handleCancel"
              :disabled="loading"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200 disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              @click="handlePasswordChange"
              :disabled="loading || !isFormValid"
              :class="[
                'px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50',
                props.isFirstLogin 
                  ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' 
                  : 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500'
              ]"
            >
              <span v-if="loading" class="flex items-center">
                <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ props.isFirstLogin ? 'Création en cours...' : 'Changement en cours...' }}
              </span>
              <span v-else>
                {{ props.isFirstLogin ? 'Créer le mot de passe' : 'Changer le mot de passe' }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useAuthStore } from '@/stores/auth.store'
import { useToast } from '@/composables/useToast'

interface Props {
  isOpen: boolean
  passwordExpiresAt?: string
  isFirstLogin?: boolean
}

interface Emits {
  (e: 'close'): void
  (e: 'success'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const authStore = useAuthStore()
const { success: showToast } = useToast()

const form = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})

const showCurrentPassword = ref(false)
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)
const loading = ref(false)
const error = ref('')

const isFormValid = computed(() => {
  return form.value.currentPassword && 
         form.value.newPassword && 
         form.value.confirmPassword &&
         form.value.newPassword === form.value.confirmPassword &&
         form.value.newPassword.length >= 8
})

// Indicateur de puissance du mot de passe
const passwordStrength = computed(() => {
  const password = form.value.newPassword
  if (!password) return { label: '', percentage: 0, color: '', bgColor: '', requirements: '' }

  let score = 0
  let requirements = []

  // Longueur
  if (password.length >= 8) {
    score += 20
  } else {
    requirements.push('Au moins 8 caractères')
  }

  // Majuscules
  if (/[A-Z]/.test(password)) {
    score += 20
  } else {
    requirements.push('Une majuscule')
  }

  // Minuscules
  if (/[a-z]/.test(password)) {
    score += 20
  } else {
    requirements.push('Une minuscule')
  }

  // Chiffres
  if (/\d/.test(password)) {
    score += 20
  } else {
    requirements.push('Un chiffre')
  }

  // Symboles
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    score += 20
  } else {
    requirements.push('Un symbole')
  }

  // Longueur supplémentaire
  if (password.length >= 12) score += 10
  if (password.length >= 16) score += 10

  let label, color, bgColor
  if (score < 40) {
    label = 'Très faible'
    color = 'text-red-600'
    bgColor = 'bg-red-500'
  } else if (score < 60) {
    label = 'Faible'
    color = 'text-orange-600'
    bgColor = 'bg-orange-500'
  } else if (score < 80) {
    label = 'Moyen'
    color = 'text-yellow-600'
    bgColor = 'bg-yellow-500'
  } else if (score < 90) {
    label = 'Fort'
    color = 'text-blue-600'
    bgColor = 'bg-blue-500'
  } else {
    label = 'Très fort'
    color = 'text-green-600'
    bgColor = 'bg-green-500'
  }

  return {
    label,
    percentage: Math.min(score, 100),
    color,
    bgColor,
    requirements: requirements.length > 0 ? `Manque: ${requirements.join(', ')}` : 'Tous les critères sont remplis'
  }
})

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const validatePassword = (password: string) => {
  const minLength = 8
  const hasUpperCase = /[A-Z]/.test(password)
  const hasLowerCase = /[a-z]/.test(password)
  const hasNumbers = /\d/.test(password)
  const hasSymbols = /[!@#$%^&*(),.?":{}|<>]/.test(password)
  
  return password.length >= minLength && hasUpperCase && hasLowerCase && hasNumbers && hasSymbols
}

// Générateur de mot de passe
const generatePassword = () => {
  const length = 16
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const lowercase = 'abcdefghijklmnopqrstuvwxyz'
  const numbers = '0123456789'
  const symbols = '!@#$%^&*(),.?":{}|<>'
  
  let password = ''
  
  // Assurer au moins un caractère de chaque type
  password += uppercase[Math.floor(Math.random() * uppercase.length)]
  password += lowercase[Math.floor(Math.random() * lowercase.length)]
  password += numbers[Math.floor(Math.random() * numbers.length)]
  password += symbols[Math.floor(Math.random() * symbols.length)]
  
  // Remplir le reste avec des caractères aléatoires
  const allChars = uppercase + lowercase + numbers + symbols
  for (let i = password.length; i < length; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)]
  }
  
  // Mélanger le mot de passe
  password = password.split('').sort(() => Math.random() - 0.5).join('')
  
  // Mettre à jour le formulaire
  form.value.newPassword = password
  form.value.confirmPassword = password
  
  // Afficher les mots de passe temporairement
  showNewPassword.value = true
  showConfirmPassword.value = true
  
  // Masquer après 3 secondes
  setTimeout(() => {
    showNewPassword.value = false
    showConfirmPassword.value = false
  }, 3000)
}

const handlePasswordChange = async () => {
  if (!isFormValid.value) {
    error.value = 'Veuillez remplir tous les champs correctement'
    return
  }

  if (!validatePassword(form.value.newPassword)) {
    error.value = 'Le mot de passe doit contenir au moins 8 caractères avec des majuscules, minuscules, chiffres et symboles'
    return
  }

  if (form.value.newPassword === form.value.currentPassword) {
    error.value = 'Le nouveau mot de passe doit être différent de l\'ancien'
    return
  }

  loading.value = true
  error.value = ''

  try {
    await authStore.changePassword(
      form.value.currentPassword,
      form.value.newPassword
    )

    showToast('Mot de passe changé avec succès', 'Succès')
    emit('success')
    resetForm()
  } catch (err: any) {
    error.value = err.message || 'Erreur lors du changement de mot de passe'
  } finally {
    loading.value = false
  }
}

const handleCancel = () => {
  // Si c'est la première connexion ou un changement forcé, ne pas permettre la fermeture
  if (props.isFirstLogin || !props.isOpen) {
    return
  }
  
  if (!loading.value) {
    resetForm()
    emit('close')
  }
}

const handleOverlayClick = () => {
  // Si c'est la première connexion ou un changement forcé, ne pas permettre la fermeture en cliquant sur l'overlay
  if (props.isFirstLogin) {
    return
  }
  
  if (!loading.value) {
    handleCancel()
  }
}

const resetForm = () => {
  form.value = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  }
  error.value = ''
  showCurrentPassword.value = false
  showNewPassword.value = false
  showConfirmPassword.value = false
}

// Réinitialiser le formulaire quand le modal s'ouvre
watch(() => props.isOpen, (isOpen) => {
  if (isOpen) {
    resetForm()
  }
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
